import { useState, useEffect } from 'react'
import { 
  FolderIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  UsersIcon,
  UserPlusIcon,
  EyeIcon,
  ArchiveBoxIcon,
  EyeSlashIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline'
import { useToast } from '../ToastContainer'
import { CustomRole } from '@/types'
import { getProjectAvailableRoles } from '../../services/api/role'

interface Project {
  id: string
  name: string
  key: string
  description?: string
  icon?: string
  archived: boolean
  allowPublicView: boolean
  createdAt: string
  updatedAt: string
  members: Array<{
    id: string
    role: string
    joinedAt: string
    user: {
      id: string
      name: string
      email: string
      avatar?: string
    }
  }>
  _count: {
    issues: number
    members: number
  }
}

interface User {
  id: string
  name: string
  email: string
  avatar?: string
  enabled: boolean
}

interface ProjectManagementState {
  projects: Project[]
  allUsers: User[]
  loading: boolean
  error: string | null
  editingProjectId: string | null
  editingField: string | null
  editingValue: string
  showMemberModal: boolean
  managingProjectId: string | null
  selectedUserIds: string[]
  selectedRole: string
  selectedRoleId: string
  availableRoles: CustomRole[]
  rolesLoading: boolean
  membersLoading: boolean
}

const ProjectManagement = () => {
  const { showSuccess, showError } = useToast()
  
  const [state, setState] = useState<ProjectManagementState>({
    projects: [],
    allUsers: [],
    loading: true,
    error: null,
    editingProjectId: null,
    editingField: null,
    editingValue: '',
    showMemberModal: false,
    managingProjectId: null,
    selectedUserIds: [],
    selectedRole: '',
    selectedRoleId: '',
    availableRoles: [],
    rolesLoading: false,
    membersLoading: false
  })

  useEffect(() => {
    Promise.all([
      fetchProjects(),
      fetchUsers()
    ])
  }, [])

  const fetchProjects = async () => {
    try {
      const response = await fetch('/api/projects/admin/all?includeArchived=true', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      })

      if (!response.ok) {
        throw new Error('獲取專案列表失敗')
      }

      const data = await response.json()
      setState(prev => ({ ...prev, projects: data.data?.projects || [], loading: false }))
    } catch (error: any) {
      setState(prev => ({ 
        ...prev, 
        error: error.message || '獲取專案列表失敗', 
        loading: false 
      }))
    }
  }

  const fetchUsers = async () => {
    try {
      const response = await fetch('/api/admin/users', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      })

      if (!response.ok) {
        throw new Error('獲取使用者列表失敗')
      }

      const data = await response.json()
      setState(prev => ({ ...prev, allUsers: data.data?.users || [] }))
    } catch (error: any) {
      console.error('獲取使用者列表失敗:', error)
    }
  }

  const fetchProjectRoles = async (projectId: string) => {
    try {
      setState(prev => ({ ...prev, rolesLoading: true }))
      const response = await getProjectAvailableRoles(projectId)
      
      if (response.success) {
        const roles = response.data?.roles || []
        setState(prev => ({ 
          ...prev, 
          availableRoles: roles,
          rolesLoading: false
        }))
        
        // 設定預設角色（優先順序：一般成員 > 成員 > 第一個角色）
        let defaultRole = roles.find(role => 
          role.name.includes('一般成員')
        )
        
        if (!defaultRole) {
          defaultRole = roles.find(role => 
            role.name.includes('成員')
          )
        }
        
        if (!defaultRole && roles.length > 0) {
          defaultRole = roles[0] // 如果找不到特定角色，使用第一個
        }
        
        if (defaultRole) {
          setState(prev => ({ 
            ...prev, 
            selectedRoleId: defaultRole.id,
            selectedRole: defaultRole.name
          }))
        } else {
          console.warn('無法找到預設角色，可用角色:', roles)
        }
      } else {
        throw new Error(response.error || '獲取專案角色失敗')
      }
    } catch (error: any) {
      console.error('獲取專案角色失敗:', error)
      showError('獲取角色失敗', error.message)
      setState(prev => ({ ...prev, rolesLoading: false }))
    }
  }

  const updateProject = async (projectId: string, updateData: any) => {
    try {
      const response = await fetch(`/api/projects/admin/${projectId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(updateData)
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || '更新專案失敗')
      }

      showSuccess('專案更新成功')
      setState(prev => ({ 
        ...prev, 
        editingProjectId: null, 
        editingField: null 
      }))
      await fetchProjects()
    } catch (error: any) {
      showError('更新失敗', error.message)
    }
  }

  const toggleProjectArchive = async (projectId: string, archived: boolean) => {
    await updateProject(projectId, { archived })
  }

  const togglePublicView = async (projectId: string, allowPublicView: boolean) => {
    await updateProject(projectId, { allowPublicView })
  }

  const openMemberModal = async (projectId: string) => {
    setState(prev => ({ 
      ...prev, 
      showMemberModal: true, 
      managingProjectId: projectId,
      selectedUserIds: [],
      selectedRole: '',
      selectedRoleId: ''
    }))
    // 載入專案可用角色
    await fetchProjectRoles(projectId)
  }

  const closeMemberModal = () => {
    setState(prev => ({ 
      ...prev, 
      showMemberModal: false, 
      managingProjectId: null,
      selectedUserIds: [],
      selectedRole: '',
      selectedRoleId: '',
      availableRoles: []
    }))
  }

  const addMembersToProject = async () => {
    if (!state.managingProjectId || state.selectedUserIds.length === 0 || !state.selectedRoleId) {
      if (!state.selectedRoleId) {
        showError('添加失敗', '請選擇角色')
      }
      return
    }

    setState(prev => ({ ...prev, membersLoading: true }))
    try {
      const response = await fetch(`/api/projects/admin/${state.managingProjectId}/members`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ 
          userIds: state.selectedUserIds,
          roleId: state.selectedRoleId
        })
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || '添加成員失敗')
      }

      const data = await response.json()
      showSuccess(data.message || '成員添加成功')
      closeMemberModal()
      await fetchProjects()
    } catch (error: any) {
      showError('操作失敗', error.message)
    } finally {
      setState(prev => ({ ...prev, membersLoading: false }))
    }
  }

  const removeMember = async (projectId: string, userId: string) => {
    if (!confirm('確定要移除此成員嗎？')) return

    try {
      const response = await fetch(`/api/projects/admin/${projectId}/members/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || '移除成員失敗')
      }

      showSuccess('成員移除成功')
      await fetchProjects()
    } catch (error: any) {
      showError('移除失敗', error.message)
    }
  }

  const startEdit = (projectId: string, field: string, currentValue: string) => {
    setState(prev => ({
      ...prev,
      editingProjectId: projectId,
      editingField: field,
      editingValue: currentValue
    }))
  }

  const cancelEdit = () => {
    setState(prev => ({
      ...prev,
      editingProjectId: null,
      editingField: null,
      editingValue: ''
    }))
  }

  const saveEdit = (projectId: string, field: string) => {
    updateProject(projectId, { [field]: state.editingValue })
  }

  const getRoleLabel = (member: any) => {
    // 如果成員有角色資訊，顯示角色名稱
    if (member.role?.name) {
      return member.role.name
    }
    // 否則顯示 roleId（暫時性的回退方案）
    return member.roleId || '未知角色'
  }

  const getRoleColor = (member: any) => {
    // 如果有自訂角色顏色，使用它
    if (member.role?.color) {
      const color = member.role.color
      // 將十六進制顏色轉換為 Tailwind 類別
      if (color.includes('purple')) return 'bg-purple-100 text-purple-800'
      if (color.includes('red')) return 'bg-red-100 text-red-800'
      if (color.includes('blue')) return 'bg-blue-100 text-blue-800'
      if (color.includes('green')) return 'bg-green-100 text-green-800'
      if (color.includes('yellow')) return 'bg-yellow-100 text-yellow-800'
    }
    
    // 根據角色名稱提供預設顏色
    const roleName = member.role?.name || ''
    if (roleName.includes('主委') || roleName.includes('擁有者') || roleName.includes('經理')) {
      return 'bg-purple-100 text-purple-800'
    }
    if (roleName.includes('管理') || roleName.includes('副理')) {
      return 'bg-red-100 text-red-800'
    }
    if (roleName.includes('開發') || roleName.includes('執行')) {
      return 'bg-blue-100 text-blue-800'
    }
    if (roleName.includes('監督') || roleName.includes('監察')) {
      return 'bg-green-100 text-green-800'
    }
    
    return 'bg-gray-100 text-gray-800'
  }

  const toggleUserSelection = (userId: string) => {
    setState(prev => ({
      ...prev,
      selectedUserIds: prev.selectedUserIds.includes(userId)
        ? prev.selectedUserIds.filter(id => id !== userId)
        : [...prev.selectedUserIds, userId]
    }))
  }

  // 獲取可添加的使用者（排除已是成員的）
  const getAvailableUsers = () => {
    if (!state.managingProjectId) return []
    
    const project = state.projects.find(p => p.id === state.managingProjectId)
    const memberUserIds = project?.members.map(m => m.user.id) || []
    
    return state.allUsers.filter(user => 
      user.enabled && !memberUserIds.includes(user.id)
    )
  }

  if (state.loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (state.error) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">載入失敗</h3>
        <p className="mt-1 text-sm text-gray-500">{state.error}</p>
        <button
          onClick={fetchProjects}
          className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
        >
          重新載入
        </button>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                <FolderIcon className="w-8 h-8 mr-3 text-blue-600" />
                專案管理
              </h1>
              <p className="text-base text-gray-600 mt-1">管理所有專案和成員</p>
            </div>
            <button
              onClick={fetchProjects}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center text-sm"
            >
              <ArrowPathIcon className="w-4 h-4 mr-2" />
              重新載入
            </button>
          </div>
        </div>
      </div>

      {/* Projects List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">專案列表 ({state.projects.length})</h2>
        </div>
        
        <div className="divide-y divide-gray-200">
          {state.projects.map((project) => (
            <div key={project.id} className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-2">
                      {/* Project Icon */}
                      {project.icon ? (
                        <img src={project.icon} alt={project.name} className="w-8 h-8 rounded" />
                      ) : (
                        <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center">
                          <FolderIcon className="w-5 h-5 text-blue-600" />
                        </div>
                      )}
                      
                      {/* Project Name */}
                      {state.editingProjectId === project.id && state.editingField === 'name' ? (
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={state.editingValue}
                            onChange={(e) => setState(prev => ({ ...prev, editingValue: e.target.value }))}
                            className="text-lg font-medium border border-gray-300 rounded px-2 py-1"
                            autoFocus
                          />
                          <button
                            onClick={() => saveEdit(project.id, 'name')}
                            className="text-green-600 hover:text-green-700"
                          >
                            <CheckIcon className="w-5 h-5" />
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="text-red-600 hover:text-red-700"
                          >
                            <XMarkIcon className="w-5 h-5" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <h3 className="text-lg font-medium text-gray-900">{project.name}</h3>
                          <button
                            onClick={() => startEdit(project.id, 'name', project.name)}
                            className="text-gray-400 hover:text-blue-600"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                      
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {project.key}
                      </span>
                    </div>
                    
                    {/* Status Badges */}
                    <div className="flex items-center space-x-2">
                      {project.archived && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          已封存
                        </span>
                      )}
                      {project.allowPublicView && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          公開瀏覽
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Description */}
                  {state.editingProjectId === project.id && state.editingField === 'description' ? (
                    <div className="mt-2 flex items-start space-x-2">
                      <textarea
                        value={state.editingValue}
                        onChange={(e) => setState(prev => ({ ...prev, editingValue: e.target.value }))}
                        className="text-sm border border-gray-300 rounded px-2 py-1 w-full resize-none"
                        rows={3}
                        autoFocus
                      />
                      <div className="flex flex-col space-y-1">
                        <button
                          onClick={() => saveEdit(project.id, 'description')}
                          className="text-green-600 hover:text-green-700"
                        >
                          <CheckIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="text-red-600 hover:text-red-700"
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-2 flex items-start space-x-2">
                      <p className="text-sm text-gray-600 flex-1">
                        {project.description || '無描述'}
                      </p>
                      <button
                        onClick={() => startEdit(project.id, 'description', project.description || '')}
                        className="text-gray-400 hover:text-blue-600"
                      >
                        <PencilIcon className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                  
                  {/* Stats */}
                  <div className="mt-3 flex items-center space-x-6 text-sm text-gray-500">
                    <span className="flex items-center">
                      <UsersIcon className="w-4 h-4 mr-1" />
                      {project._count.members} 位成員
                    </span>
                    <span>{project._count.issues} 個議題</span>
                    <span>建立於 {new Date(project.createdAt).toLocaleDateString('zh-TW')}</span>
                  </div>

                  {/* Members */}
                  {project.members.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">專案成員:</h4>
                      <div className="flex flex-wrap gap-2">
                        {project.members.slice(0, 5).map((member) => (
                          <div key={member.id} className="flex items-center bg-gray-50 rounded-lg px-3 py-1">
                            <div className="flex items-center space-x-2">
                              {member.user.avatar ? (
                                <img src={member.user.avatar} alt={member.user.name} className="w-6 h-6 rounded-full" />
                              ) : (
                                <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center">
                                  <span className="text-xs font-medium text-gray-600">
                                    {member.user.name.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                              )}
                              <span className="text-sm text-gray-700">{member.user.name}</span>
                              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${getRoleColor(member)}`}>
                                {getRoleLabel(member)}
                              </span>
                            </div>
                            <button
                              onClick={() => removeMember(project.id, member.user.id)}
                              className="ml-2 text-gray-400 hover:text-red-500"
                            >
                              <XMarkIcon className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                        {project.members.length > 5 && (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600">
                            +{project.members.length - 5} 更多
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center space-x-2 ml-4">
                  <button
                    onClick={() => openMemberModal(project.id)}
                    className="text-blue-600 hover:text-blue-700 p-2"
                    title="管理成員"
                  >
                    <UserPlusIcon className="w-5 h-5" />
                  </button>
                  
                  <button
                    onClick={() => togglePublicView(project.id, !project.allowPublicView)}
                    className={`p-2 ${project.allowPublicView ? 'text-green-600 hover:text-green-700' : 'text-gray-400 hover:text-gray-600'}`}
                    title={project.allowPublicView ? '關閉公開瀏覽' : '開啟公開瀏覽'}
                  >
                    {project.allowPublicView ? <EyeIcon className="w-5 h-5" /> : <EyeSlashIcon className="w-5 h-5" />}
                  </button>
                  
                  <button
                    onClick={() => toggleProjectArchive(project.id, !project.archived)}
                    className={`p-2 ${project.archived ? 'text-orange-600 hover:text-orange-700' : 'text-gray-400 hover:text-gray-600'}`}
                    title={project.archived ? '取消封存' : '封存專案'}
                  >
                    <ArchiveBoxIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add Members Modal */}
      {state.showMemberModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">添加專案成員</h3>
                <button
                  onClick={closeMemberModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Role Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    選擇角色
                  </label>
                  {state.rolesLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <ArrowPathIcon className="w-5 h-5 animate-spin text-gray-400" />
                      <span className="ml-2 text-sm text-gray-500">載入角色中...</span>
                    </div>
                  ) : (
                    <select
                      value={state.selectedRoleId}
                      onChange={(e) => {
                        const selectedRole = state.availableRoles.find(role => role.id === e.target.value)
                        setState(prev => ({ 
                          ...prev, 
                          selectedRoleId: e.target.value,
                          selectedRole: selectedRole?.name || ''
                        }))
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">請選擇角色</option>
                      {state.availableRoles.map(role => (
                        <option key={role.id} value={role.id}>
                          {role.name}
                          {role.description && ` - ${role.description}`}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* User Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    選擇使用者
                  </label>
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {getAvailableUsers().map((user) => (
                      <label key={user.id} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={state.selectedUserIds.includes(user.id)}
                          onChange={() => toggleUserSelection(user.id)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <div className="ml-3 flex items-center space-x-3">
                          {user.avatar ? (
                            <img src={user.avatar} alt={user.name} className="w-8 h-8 rounded-full" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center">
                              <span className="text-sm font-medium text-gray-600">
                                {user.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                          <div>
                            <p className="text-sm font-medium text-gray-900">{user.name}</p>
                            <p className="text-xs text-gray-500">{user.email}</p>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                  
                  {getAvailableUsers().length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-4">
                      沒有可添加的使用者
                    </p>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={closeMemberModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  取消
                </button>
                <button
                  onClick={addMembersToProject}
                  disabled={state.membersLoading || state.selectedUserIds.length === 0 || !state.selectedRoleId}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {state.membersLoading ? '添加中...' : `添加 ${state.selectedUserIds.length} 位成員`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProjectManagement
